// Token store.
//
// Access token lives in MEMORY ONLY — it dies with the tab or the app, which
// keeps a short-lived credential out of any persistent store. The refresh token is
// persisted so a reload can re-authenticate silently.
//
// Phase 10 swapped the persistence layer: on the web it is still localStorage, on
// native it is the platform keystore via @capacitor/preferences. The migration
// plan is explicit that tokens must NOT sit in localStorage in the app.
//
// THE SHAPE PROBLEM, and why it is solved this way: the platform store is ASYNC
// while every caller of `getRefreshToken()` is sync, and making the getter async
// would ripple through the whole client. Instead the refresh token is mirrored in
// a module-level variable — hydrated ONCE at boot by `hydrateTokens()`, and
// written through in the background on every change. Callers keep their sync
// interface; the only requirement is that `hydrateTokens()` is awaited before the
// first request, which `boot()` does.

import { deleteSecure, loadSecure, saveSecure, type StorageScope } from '../platform.js';
import type { MasqueradeState } from '@msc/shared';

const REFRESH_KEY = 'msc.refresh';

/**
 * Which store the current session's refresh token belongs in.
 *
 * Set at login from the "Stay logged in" checkbox, and re-derived at boot from
 * whichever store the token was actually found in. It has to be remembered,
 * because rotation writes a NEW token on every refresh — if that write defaulted
 * to persistent, a deliberately session-scoped login would silently promote
 * itself to a 30-day one the first time the access token expired.
 */
let scope: StorageScope = 'persistent';

let accessToken: string | null = null;
let accessExpiresAt = 0;

/**
 * The in-memory mirror of the persisted refresh token.
 *
 * `undefined` means "not hydrated yet" and is distinct from `null`, which means
 * "hydrated, and there is no token". Without that distinction a call before
 * hydration looks identical to being signed out, and the app would bounce a
 * returning user to the login screen on every cold start.
 */
let refreshToken: string | null | undefined;

/**
 * The masquerade this session is inside, or null.
 *
 * Kept HERE rather than in the client module because it is session state in
 * exactly the sense the tokens are: it has to be established before the first
 * request, it has to survive a reload, and the refresh call has to be able to
 * read it. It is not a secret — the signed access token is the authority — but it
 * shares the persistence path so a native app keeps it in the same place.
 *
 * `undefined` means not hydrated yet, distinct from `null` meaning "hydrated, not
 * masquerading", for the same reason the refresh token draws that distinction.
 */
let masquerade: MasqueradeState | null | undefined;

const MASQ_KEY = 'msc.masquerade';

export function getMasquerade(): MasqueradeState | null {
  return masquerade ?? null;
}

/**
 * Set or clear the masquerade, writing through to the platform store.
 *
 * Written through rather than kept in memory only: an admin who reloads the page
 * mid-masquerade must not silently land back in their own account while the red
 * banner disappears — that is precisely the confusion the banner exists to
 * prevent.
 */
export function setMasquerade(state: MasqueradeState | null): void {
  masquerade = state;
  if (state === null) {
    void deleteSecure(MASQ_KEY);
    return;
  }
  void saveSecure(MASQ_KEY, JSON.stringify(state), scope);
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** True when there is a token that is not within 30s of expiry. */
export function hasFreshAccessToken(): boolean {
  return accessToken !== null && Date.now() < accessExpiresAt - 30_000;
}

export function setAccessToken(token: string, expiresInSeconds: number): void {
  accessToken = token;
  accessExpiresAt = Date.now() + expiresInSeconds * 1000;
}

/**
 * Read the persisted refresh token from the in-memory mirror.
 *
 * Returns null before hydration. Call `hydrateTokens()` at boot — `boot()` awaits
 * it before the router starts, so no view can observe the un-hydrated state.
 */
export function getRefreshToken(): string | null {
  return refreshToken ?? null;
}

/**
 * Store a refresh token.
 *
 * `stayLoggedIn` is passed only at LOGIN. Rotation calls this without it and
 * inherits the session's existing scope — see the note on `scope` above.
 */
export function setRefreshToken(token: string, stayLoggedIn?: boolean): void {
  const scopeChanged = stayLoggedIn !== undefined;
  if (scopeChanged) scope = stayLoggedIn ? 'persistent' : 'session';
  refreshToken = token;

  // Fire-and-forget as a whole, but STRICTLY ORDERED inside: on native both
  // helpers await a dynamic import, so an unsequenced delete-then-save can land
  // in the opposite order and erase the token it was meant to replace.
  void (async () => {
    // At login only, drop any copy in the other store — otherwise an earlier
    // "stay logged in" token outlives a later session-only login on the same
    // browser, and closing the tab would not sign you out after all.
    if (scopeChanged) await deleteSecure(REFRESH_KEY);
    await saveSecure(REFRESH_KEY, token, scope);
  })();
}

/**
 * Re-read the persisted refresh token into the mirror.
 *
 * The mirror is hydrated once at boot and otherwise only ever written by THIS
 * tab, so it goes stale the moment another tab rotates the token. Presenting a
 * stale token is not a soft failure: rotation is single-use, and a token matching
 * no row is treated as theft and revokes the user's whole family — see
 * `refreshSession` in `client.ts`. Call this inside the cross-tab lock, before
 * rotating.
 */
export async function resyncRefreshToken(): Promise<void> {
  const stored = await loadSecure(REFRESH_KEY, scope);
  // Never clobber a live mirror with null. On native, `session` scope persists
  // nothing by design and the mirror IS the token, so a null here means "nothing
  // was stored", not "the session ended".
  if (stored !== null) refreshToken = stored;
}

/**
 * Adopt a refresh token rotated by another tab.
 *
 * Web only, and persistent scope only: `session` scope is sessionStorage, which
 * is per-tab and cannot collide, and native has a single webview with no second
 * tab to race. A `storage` event fires only in the tabs that did NOT write, which
 * is exactly the set that needs to hear about it.
 *
 * Removals are deliberately ignored. Login with a changed "stay logged in" scope
 * deletes before it saves, so acting on the null would tear down a session that
 * is mid-establishment; and a real logout elsewhere already revoked the token
 * server-side, so this tab's next call fails and clears on its own.
 */
function installCrossTabSync(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('storage', (ev) => {
    if (ev.key !== REFRESH_KEY || ev.newValue === null) return;
    try {
      if (ev.storageArea !== localStorage) return;
    } catch {
      return; // private mode can throw on storage access
    }
    refreshToken = ev.newValue;
  });
}

export function clearTokens(): void {
  accessToken = null;
  accessExpiresAt = 0;
  refreshToken = null;
  // Any masquerade dies with the session it was riding on. Leaving it behind
  // would have the next login start with a banner claiming to be somebody else.
  masquerade = null;
  void deleteSecure(REFRESH_KEY);
  void deleteSecure(MASQ_KEY);
}

/**
 * Load the persisted refresh token into memory. Await this once, at boot, before
 * the first request.
 *
 * Idempotent: a second call is a no-op, so an accidental double-boot cannot
 * clobber a token set in between.
 */
export async function hydrateTokens(): Promise<void> {
  if (refreshToken !== undefined) return;
  installCrossTabSync();
  // Set below, alongside the refresh token, by hydrateMasquerade().

  // Session store FIRST. If both somehow hold a token, the session-scoped one is
  // the more recent intent — and honouring the persistent copy instead would
  // resurrect a "stay logged in" session the user had since replaced with a
  // deliberately temporary one.
  const sessionToken = await loadSecure(REFRESH_KEY, 'session');
  if (sessionToken !== null) {
    scope = 'session';
    refreshToken = sessionToken;
    await hydrateMasquerade();
    return;
  }

  scope = 'persistent';
  refreshToken = await loadSecure(REFRESH_KEY, 'persistent');
  await hydrateMasquerade();
}

/**
 * Restore a masquerade across a reload.
 *
 * Read from whichever store the refresh token came from, since the two belong to
 * the same session. A malformed value is treated as "not masquerading" rather than
 * thrown: the safe reading is always the less powerful one, and the server would
 * refuse anything else anyway.
 */
async function hydrateMasquerade(): Promise<void> {
  masquerade = null;
  if (refreshToken === null) return;
  const raw = await loadSecure(MASQ_KEY, scope);
  if (raw === null) return;
  try {
    const parsed = JSON.parse(raw) as MasqueradeState;
    if (typeof parsed?.subjectId === 'number' && typeof parsed?.actorId === 'number') {
      masquerade = { ...parsed, mode: parsed.mode === 'write' ? 'write' : 'read' };
    }
  } catch {
    masquerade = null;
  }
}
